import React, { useState, useRef, useMemo, useEffect } from 'react';
import { X, Printer, Sliders, Check, FileText, UserCheck, Sparkles, Plus, Upload, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { useSession } from '@/lib/SessionContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import imageCompression from 'browser-image-compression';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const DP_PRESETS = [
  { value: '50', label: '50% / 50% (Standar)' },
  { value: '30', label: '30% / 70%' },
  { value: '40', label: '40% / 60%' },
  { value: '70', label: '70% / 30%' },
  { value: '100', label: '100% (Lunas di Awal)' },
  { value: 'custom', label: 'Custom %' },
];

const PRESET_SPESIFIKASI_TAMBAHAN = [
  'Free sampel produk',
  'Free R&D aroma',
  'Jasa Produksi Sesuai Dengan CPKB',
  'Free Desain & Konsultasi',
  'Support Penjualan E-Commerce',
  'Pendaftaran Notifikasi BPOM',
  'Uji Stabilitas Laboratorium',
  'Sertifikasi Halal Produk'
];

const getDpText = (dpValue, customVal) => {
  if (dpValue === '100') {
    return 'Pembayaran 100% di awal (Lunas di muka sebelum naik produksi)';
  }
  const pct = dpValue === 'custom' ? (parseFloat(customVal) || 50) : parseFloat(dpValue);
  const remaining = 100 - pct;
  return `Down Payment ${pct}%, pelunasan ${remaining}% ketika selesai produksi dan barang siap dikirim`;
};

const SphPrintModal = ({ isOpen, onClose, sphData }) => {
  const printRef = useRef(null);
  const { member, companySettings } = useSession();
  const queryClient = useQueryClient();

  // Query Daftar Anggota Tim untuk Dropdown PIC
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  const picOptions = useMemo(() => {
    const list = teamMembers
      .map(m => (m.full_name || m.name || '').trim())
      .filter(Boolean);
    const unique = Array.from(new Set([...list, 'Muhammad Fadli', sphData?.pic].filter(Boolean)));
    return unique.map(name => ({ value: name, label: name }));
  }, [teamMembers, sphData?.pic]);

  // PIC & Jabatan
  const [picName, setPicName] = useState(sphData?.pic || member?.full_name || 'Muhammad Fadli');
  const [picJabatan, setPicJabatan] = useState('Account Executive');
  const [customSignature, setCustomSignature] = useState(null);

  const selectedPicMember = useMemo(() => {
    if (!picName) return null;
    const cleanPic = picName.trim().toLowerCase();
    return teamMembers.find(m => {
      const fn = (m.full_name || m.name || '').trim().toLowerCase();
      if (!fn) return false;
      return fn === cleanPic || cleanPic.startsWith(fn) || fn.startsWith(cleanPic);
    });
  }, [teamMembers, picName]);

  const activeSignatureUrl = customSignature !== null 
    ? customSignature 
    : (selectedPicMember?.signature_url || selectedPicMember?.ttd_url || companySettings?.ttd_url || '');

  // Auto-sync Jabatan saat PIC terpilih berubah
  useEffect(() => {
    setCustomSignature(null);
    if (selectedPicMember) {
      if (selectedPicMember.department) {
        setPicJabatan(selectedPicMember.department);
      } else if (selectedPicMember.position) {
        setPicJabatan(selectedPicMember.position);
      } else if (selectedPicMember.role && selectedPicMember.role !== 'default') {
        const roleMap = {
          owner: 'Direktur Utama',
          developer: 'Developer & IT Lead',
          finance: 'Finance Manager',
          marketing: 'Marketing Executive',
          production: 'Production Lead',
          gudang: 'Warehouse Supervisor',
        };
        setPicJabatan(roleMap[selectedPicMember.role] || selectedPicMember.role);
      }
    }
  }, [selectedPicMember]);

  // Tata Letak Halaman (Otomatis vs Paksa Catatan & TTD ke Hal 2)
  const [forceFooterPageBreak, setForceFooterPageBreak] = useState(false);

  // Down Payment
  const [selectedDp, setSelectedDp] = useState('50');
  const [customDpVal, setCustomDpVal] = useState('50');

  // Spesifikasi Tambahan
  const [customTambahan, setCustomTambahan] = useState(() => {
    let list = (sphData?.sph_tambahan || '').split('\n').filter(Boolean);
    if (list.length === 0) {
      list = [
        'Free sampel produk',
        'Free R&D aroma',
        'Jasa Produksi Sesuai Dengan CPKB',
        'Free Desain & Konsultasi',
        'Support Penjualan E-Commerce'
      ];
    }
    return list.join('\n');
  });

  // Catatan Penting
  const [customTerms, setCustomTerms] = useState(() => {
    let list = (sphData?.sph_terms || '').split('\n').filter(Boolean);
    if (list.length === 0) {
      list = [
        'Down Payment 50%, pelunasan 50% ketika selesai produksi dan barang siap dikirim',
        'Belum termasuk ongkos kirim finished goods ke lokasi gudang costumer',
        'Surat penawaran harga ini berlaku 3 (tiga) hari setelah diterbitkan.'
      ];
    }
    return list.join('\n');
  });

  const handleDpChange = (val) => {
    setSelectedDp(val);
    const newDpText = getDpText(val, customDpVal);
    
    const lines = customTerms.split('\n').filter(Boolean);
    if (lines.length > 0) {
      lines[0] = newDpText;
      setCustomTerms(lines.join('\n'));
    } else {
      setCustomTerms([
        newDpText,
        'Belum termasuk ongkos kirim finished goods ke lokasi gudang costumer',
        'Surat penawaran harga ini berlaku 3 (tiga) hari setelah diterbitkan.'
      ].join('\n'));
    }
  };

  const handleCustomValChange = (val) => {
    setCustomDpVal(val);
    const newDpText = getDpText('custom', val);
    const lines = customTerms.split('\n').filter(Boolean);
    if (lines.length > 0) {
      lines[0] = newDpText;
      setCustomTerms(lines.join('\n'));
    }
  };

  const toggleSpesifikasiItem = (itemText) => {
    const currentList = customTambahan.split('\n').filter(Boolean);
    const exists = currentList.some(item => item.trim().toLowerCase() === itemText.trim().toLowerCase());
    
    if (exists) {
      setCustomTambahan(currentList.filter(item => item.trim().toLowerCase() !== itemText.trim().toLowerCase()).join('\n'));
    } else {
      setCustomTambahan([...currentList, itemText].join('\n'));
    }
  };

  const triggerDirectPrint = () => {
    const docTitle = `Cetak_SPH_${sphData?.id || 'Dokumen'}`;
    document.title = docTitle;

    const styleEl = document.createElement('style');
    styleEl.id = 'sph-print-style';
    styleEl.innerHTML = `
      @media print {
        body > #root { display: none !important; }
        #sph-print-overlay { 
          display: block !important; 
          position: static !important; 
          width: 100% !important; 
          background: white !important; 
          z-index: 999999 !important;
          visibility: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #sph-print-overlay * {
          visibility: visible !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @page { 
          size: A4 portrait; 
          margin: 6mm 8mm; 
        }
        html, body { 
          background: white !important; 
          margin: 0 !important; 
          padding: 0 !important; 
          height: auto !important; 
          overflow: visible !important; 
        }

        /* Hindari pemotongan di tengah kartu / baris tabel (daripada kepotong mending kebawahin) */
        .sph-no-break,
        .sph-spec-box,
        .sph-footer-block,
        .sph-notes-box,
        .sph-signature-box,
        table, tr, td, th {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        /* Terapkan display table pada footer block di media print agar Chromium patuh tidak memotong kartu tanda tangan secara horizontal */
        .sph-footer-block {
          display: table !important;
          width: 100% !important;
          table-layout: fixed !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          overflow: visible !important;
          margin-top: 14px !important;
        }

        .sph-footer-block > .sph-notes-box {
          display: table-cell !important;
          width: 58% !important;
          vertical-align: top !important;
          padding-right: 16px !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          overflow: visible !important;
        }

        .sph-footer-block > .sph-signature-box {
          display: table-cell !important;
          width: 42% !important;
          vertical-align: top !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          overflow: visible !important;
        }

        /* Jika opsi pisahkan halaman aktif atau overflow, kebawahin ke halaman baru secara utuh */
        .sph-force-page-break {
          page-break-before: always !important;
          break-before: page !important;
          margin-top: 15mm !important;
        }
      }
      @media screen {
        #sph-print-overlay { display: none !important; }
      }
    `;
    document.head.appendChild(styleEl);

    const overlay = document.createElement('div');
    overlay.id = 'sph-print-overlay';

    if (printRef.current) {
      overlay.innerHTML = printRef.current.outerHTML;
    }
    document.body.appendChild(overlay);

    setTimeout(() => {
      if (printRef.current && overlay) {
        overlay.innerHTML = printRef.current.outerHTML;
      }
      window.print();
    }, 100);

    const handleAfterPrint = () => {
      if (styleEl && styleEl.parentNode) document.head.removeChild(styleEl);
      if (overlay && overlay.parentNode) document.body.removeChild(overlay);
      window.removeEventListener('afterprint', handleAfterPrint);
      if (onClose) onClose();
    };

    window.addEventListener('afterprint', handleAfterPrint);
  };

  if (!isOpen || !sphData) return null;

  const fmt = (num) => Number(num || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtQty = (num) => Number(num || 0).toLocaleString('id-ID', { maximumFractionDigits: 2 });
  const items = typeof sphData?.products === 'string' ? JSON.parse(sphData.products) : (sphData?.products || []);
  const rawLegalitas = typeof sphData?.legalitas === 'string' ? JSON.parse(sphData.legalitas) : (sphData?.legalitas || []);
  const legalitasItems = (rawLegalitas || []).filter(l => l && (l.harga_jual || l.nama));

  let maxBibit = 0;
  let totalQty = 0;
  let boxNames = new Set();
  let botolSet = new Set();

  items.forEach(p => {
    totalQty += parseFloat(String(p.qty || 0).replace(',', '.'));

    (p.racikan || []).forEach(r => {
      if ((r.kategori_bahan || r.kategori || '').toLowerCase() === 'bibit') {
         let pct = parseFloat(String(r.persentase).replace(',', '.')) || 0;
         if (pct > maxBibit) maxBibit = pct;
      }
    });

    (p.komponen_tambahan || []).forEach(k => {
         if (k.dari_client) return;
         const nameStr = (k.item_nama || k.nama_item || k.nama || '');
         const name = nameStr.toLowerCase();
         if (name.includes('box') || name.includes('sablon') || name.includes('color') || name.includes('warna')) {
             boxNames.add(nameStr);
         }
    });

    let kemasan = [];
    if (p.is_fullset) {
      kemasan.push(`Fullset : ${p.fullset_nama || 'Kemasan Fullset'}`);
    } else {
      if (p.botol_nama && p.botol_nama !== 'Botol Dari Client') kemasan.push(`Botol : ${p.botol_nama}`);
      if (p.tutup_nama && p.tutup_nama !== 'Tutup Dari Client') kemasan.push(`Tutup : ${p.tutup_nama}`);
      if (p.spray_nama && p.spray_nama !== 'Spray Dari Client') kemasan.push(`Spray : ${p.spray_nama}`);
    }
    
    if (kemasan.length > 0) {
      kemasan.forEach(k => botolSet.add(k));
    } else {
      let ukuran = p.botol_ukuran_label || p.botol_ukuran_aktual || p.ukuran_botol_ml || 30;
      botolSet.add(`Botol Kaca ${ukuran}ml + Tutup`);
    }
  });

  let jenisProduk = 'Eau De Parfume';
  if (maxBibit >= 20) jenisProduk = 'Extrait de Parfum';
  else if (maxBibit > 10 && maxBibit < 20) jenisProduk = 'Eau De Parfume';
  else if (maxBibit > 0 && maxBibit <= 10) jenisProduk = 'Eau De Toilette / Body Mist';

  let kemasanText = Array.from(botolSet);
  let showKemasanInTable = false;
  if (kemasanText.length > 3) {
      kemasanText = ['Kemasan : Bervariasi (Lihat Detail di Tabel)'];
      showKemasanInTable = true;
  }

  const autoSpesifikasiList = [
    `Jenis Produk: ${jenisProduk}`,
    `Qty : ${fmtQty(totalQty)} pcs`,
    ...Array.from(boxNames),
    `${items.length} Varian aroma`,
    ...kemasanText,
    ...legalitasItems.map(l => `Legalitas: ${l.nama}${l.qty_variant > 1 ? ` (${l.qty_variant} Varian)` : ''}`)
  ];

  const tambahanList = customTambahan.split('\n').filter(Boolean);
  const termsList = customTerms.split('\n').filter(Boolean);

  return (
    <>
      {/* Pengaturan Cetak SPH Modal (User Interaktif) */}
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 print:hidden">
        <div className="bg-white rounded-3xl p-6 sm:p-7 max-w-xl w-full shadow-2xl border border-[#E6D5B8] max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#F9F5EC] flex items-center justify-center text-[#8C6208]">
                <Printer className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-[#302621]">Pengaturan Cetak SPH</h3>
                <p className="text-xs text-[#7A695B]">{(sphData.id || '').replace('INV', 'SPH')} • {sphData.customerName?.split(' / ')[0]}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full w-8 h-8">
              <X className="w-4 h-4 text-slate-400" />
            </Button>
          </div>

          <div className="space-y-5">
            {/* PIC & Jabatan Penandatangan */}
            <div className="bg-[#F9F5EC]/60 border border-[#E6D5B8]/60 p-3.5 rounded-2xl">
              <label className="text-xs font-bold text-[#302621] mb-2 block flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5 text-[#8C6208]" /> Penanggung Jawab (PIC)
              </label>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <span className="text-[10px] text-slate-500 font-semibold block mb-1">Pilih PIC *</span>
                  <SearchableCombobox
                    value={picName}
                    onValueChange={(val) => {
                      setPicName(val);
                      const found = teamMembers.find(m => (m.full_name || m.name || '').trim().toLowerCase() === val?.trim().toLowerCase());
                      if (found?.department) {
                        setPicJabatan(found.department);
                      } else if (found?.position) {
                        setPicJabatan(found.position);
                      } else if (found?.role && found.role !== 'default') {
                        setPicJabatan(found.role.toUpperCase());
                      }
                    }}
                    options={picOptions}
                    placeholder="Pilih atau cari PIC..."
                    searchPlaceholder="Cari nama anggota tim..."
                    allowCustom={true}
                  />
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 font-semibold block mb-1">Jabatan</span>
                  <Input
                    value={picJabatan}
                    onChange={(e) => setPicJabatan(e.target.value)}
                    placeholder="Misal: Account Executive"
                    className="h-10 text-xs bg-white border-[#E6D5B8] text-[#302621]"
                  />
                </div>
              </div>

              {/* Status Tanda Tangan */}
              <div className="mt-2.5 flex items-center justify-between text-[11px] bg-white/80 px-3 py-2 rounded-xl border border-[#E6D5B8]/80">
                <span className="text-slate-600 font-semibold flex items-center gap-1.5">
                  <PenLine className="w-3.5 h-3.5 text-[#8C6208]" /> Tanda Tangan (TTD)
                </span>
                <div className="flex items-center gap-2">
                  {activeSignatureUrl ? (
                    <>
                      <img 
                        src={activeSignatureUrl} 
                        alt="TTD" 
                        className="h-6 max-w-[70px] object-contain bg-white rounded border border-amber-200 px-1" 
                        crossOrigin="anonymous" 
                      />
                      <span className="text-emerald-700 font-bold text-[10px] bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        Tersedia
                      </span>
                    </>
                  ) : (
                    <span className="text-amber-700 font-medium text-[10px] bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full italic">
                      Belum ada TTD
                    </span>
                  )}
                  <label className="cursor-pointer bg-[#8C6208] text-white hover:bg-[#724F06] text-[10px] px-2.5 py-1 rounded-lg font-bold shrink-0 flex items-center gap-1.5 shadow-xs transition-all">
                    <Upload className="w-3 h-3" />
                    <span>{activeSignatureUrl ? 'Ganti TTD' : 'Upload TTD'}</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            let processedFile = file;
                            if (file.size > 200 * 1024) {
                              processedFile = await imageCompression(file, {
                                maxSizeMB: 0.2,
                                maxWidthOrHeight: 800,
                                useWebWorker: true
                              });
                            }
                            const reader = new FileReader();
                            reader.onload = async (ev) => {
                              const b64 = ev.target.result;
                              setCustomSignature(b64);
                              if (selectedPicMember?.id) {
                                await base44.entities.TeamMember.update(selectedPicMember.id, { signature_url: b64 });
                                queryClient.invalidateQueries({ queryKey: ['team-members'] });
                              }
                            };
                            reader.readAsDataURL(processedFile);
                          } catch {
                            const reader = new FileReader();
                            reader.onload = async (ev) => {
                              const b64 = ev.target.result;
                              setCustomSignature(b64);
                              if (selectedPicMember?.id) {
                                await base44.entities.TeamMember.update(selectedPicMember.id, { signature_url: b64 });
                                queryClient.invalidateQueries({ queryKey: ['team-members'] });
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Opsi Down Payment */}
            <div>
              <label className="text-xs font-bold text-[#302621] mb-2 block flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-[#8C6208]" /> Ketentuan Down Payment (DP)
              </label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {DP_PRESETS.map((dp) => (
                  <button
                    key={dp.value}
                    type="button"
                    onClick={() => handleDpChange(dp.value)}
                    className={`py-2 px-2 rounded-xl text-xs font-semibold border transition-all ${
                      selectedDp === dp.value
                        ? 'bg-[#8C6208] text-white border-[#8C6208] shadow-sm'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-[#8C6208]/40'
                    }`}
                  >
                    {dp.label}
                  </button>
                ))}
              </div>

              {selectedDp === 'custom' && (
                <div className="flex items-center gap-2 mt-2 bg-amber-50 p-2.5 rounded-xl border border-amber-200">
                  <span className="text-xs font-bold text-amber-900">Persentase DP:</span>
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={customDpVal}
                    onChange={(e) => handleCustomValChange(e.target.value)}
                    className="w-20 h-8 text-xs bg-white border-amber-300 font-bold text-center"
                  />
                  <span className="text-xs text-amber-800">% (Pelunasan {100 - (parseFloat(customDpVal) || 0)}%)</span>
                </div>
              )}
            </div>

            {/* Spesifikasi Tambahan */}
            <div>
              <label className="text-xs font-bold text-[#302621] mb-1.5 block flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" /> Spesifikasi Tambahan
              </label>
              <p className="text-[10px] text-slate-400 mb-2">Klik tombol cepat untuk menambah/menghapus, atau ketik langsung di kolom teks.</p>
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {PRESET_SPESIFIKASI_TAMBAHAN.map((item) => {
                  const isActive = customTambahan.toLowerCase().includes(item.toLowerCase());
                  return (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleSpesifikasiItem(item)}
                      className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1 ${
                        isActive
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {isActive ? <Check className="w-3 h-3 text-emerald-600" /> : <Plus className="w-3 h-3 text-slate-400" />}
                      {item}
                    </button>
                  );
                })}
              </div>
              <Textarea
                rows={3}
                value={customTambahan}
                onChange={(e) => setCustomTambahan(e.target.value)}
                className="text-xs border-slate-200 resize-none font-sans leading-relaxed"
                placeholder="Tuliskan spesifikasi tambahan (1 baris per poin)..."
              />
            </div>

            {/* Catatan Penting / Syarat & Ketentuan */}
            <div>
              <label className="text-xs font-bold text-[#302621] mb-1.5 block flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-[#8C6208]" /> Catatan Penting & Syarat Ketentuan
              </label>
              <Textarea
                rows={3}
                value={customTerms}
                onChange={(e) => setCustomTerms(e.target.value)}
                className="text-xs border-slate-200 resize-none font-sans leading-relaxed"
                placeholder="Tuliskan catatan penting..."
              />
            </div>

            {/* Opsi Tata Letak Halaman Cetak */}
            <div className="p-3 bg-amber-50/60 border border-amber-200/80 rounded-xl space-y-1.5">
              <label className="text-xs font-bold text-[#302621] flex items-center justify-between cursor-pointer select-none">
                <span className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-[#8C6208]" /> Kebawahin Catatan & TTD ke Hal 2
                </span>
                <input 
                  type="checkbox" 
                  checked={forceFooterPageBreak} 
                  onChange={(e) => setForceFooterPageBreak(e.target.checked)}
                  className="w-4 h-4 text-[#8C6208] rounded border-amber-300 focus:ring-[#8C6208] cursor-pointer"
                />
              </label>
              <p className="text-[10px] text-slate-500 leading-relaxed">
                {forceFooterPageBreak 
                  ? 'Catatan penting & tanda tangan akan dipisahkan ke halaman 2 secara utuh.' 
                  : 'Otomatis pas 1 halaman jika muat. Jika melimpah, sistem otomatis memindahkan blok catatan & tanda tangan utuh ke halaman 2 (tidak akan terpotong setengah).'}
              </p>
            </div>
          </div>

          <div className="flex gap-2.5 mt-6 pt-4 border-t border-slate-100">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 text-xs border-slate-200"
            >
              Batal
            </Button>
            <Button 
              onClick={triggerDirectPrint} 
              className="flex-1 text-xs bg-[#8C6208] hover:bg-[#6e4c06] text-white font-bold gap-1.5 shadow-md shadow-[#8C6208]/20"
            >
              <Printer className="w-4 h-4" /> Cetak SPH Sekarang
            </Button>
          </div>
        </div>
      </div>

      {/* Actual SPH Document Content to Clone */}
      <div className="hidden">
        <div 
          ref={printRef} 
          className="sph-print-wrapper bg-white mx-auto px-4 py-2 text-black" 
          style={{ width: '100%', maxWidth: '210mm', fontFamily: 'Inter, sans-serif' }}
        >
          {/* Company Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-4 items-center">
              <img src="/logo.png" alt="Logo" className="w-14 h-14 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h2 className="text-lg font-black uppercase text-[#302621] tracking-tight mb-1">PT. SATALA DERMATECH ESSENTIAL</h2>
                <div className="flex items-start text-[9.5px] text-[#302621] max-w-[320px]">
                  <p>Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat</p>
                </div>
              </div>
            </div>
            <div className="text-right mt-0.5">
              <h1 className="text-lg font-black text-[#302621] tracking-wide leading-tight">SURAT<br/>PENAWARAN HARGA</h1>
              <div className="flex items-center justify-end mt-1">
                <div className="h-[2px] bg-[#b48a3a] w-10 mr-3"></div>
                <span className="text-[#b48a3a] font-bold tracking-widest text-xs">{(sphData.id || '').replace('INV', 'SPH')}</span>
              </div>
            </div>
          </div>

          <div className="w-full h-[2px] bg-[#b48a3a] mb-4"></div>

          {/* Info Box */}
          <div className="border border-[#e5d4b5] rounded-xl flex items-stretch mb-4 overflow-hidden bg-white sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
            <div className="flex-1 p-3.5 border-r border-[#e5d4b5]/50 flex items-start gap-3">
              <div>
                <p className="text-[9px] text-slate-500 font-bold tracking-wider mb-0.5">TANGGAL & MASA BERLAKU</p>
                <p className="font-bold text-[#302621] text-xs mb-0.5">
                  {sphData.date ? format(new Date(sphData.date), 'dd MMMM yyyy', {locale: id}) : '-'}
                </p>
                <p className="text-[9px] text-slate-500 leading-tight">
                  Berlaku 3 Hari (s.d {sphData.date ? format(new Date(new Date(sphData.date).getTime() + 3*24*60*60*1000), 'dd MMMM yyyy', {locale: id}) : '-'})
                </p>
              </div>
            </div>

            <div className="flex-[2] p-3.5 bg-[#Fdfbf7] flex items-start gap-3">
              <div>
                <p className="text-[9px] text-slate-500 font-bold tracking-wider mb-0.5">KEPADA</p>
                <p className="font-bold text-[#302621] text-xs">{(sphData.customerName || sphData.nama_brand || '-').split(' / ')[0]}</p>
                <p className="text-[8.5px] text-slate-600 mt-0.5 leading-tight">Telp. {sphData.phone || sphData.client_phone || '-'}</p>
                <p className="text-[8.5px] text-slate-600 mt-0.5 leading-tight">{sphData.customerAddress || sphData.client_address || '-'}</p>
              </div>
            </div>
          </div>

          {/* Spesifikasi Grid */}
          <div className="grid grid-cols-2 gap-5 mb-4 text-xs sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
            {/* Spesifikasi Produk */}
            <div className="sph-spec-box relative border border-[#e5d4b5] rounded-xl p-4 pt-6 bg-[#Fdfbf7]/30" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="absolute -top-3 left-4 flex items-center">
                <div className="bg-[#b48a3a] text-white py-1 px-2.5 rounded-md font-bold text-[9.5px] tracking-wider">
                  SPESIFIKASI PRODUK
                </div>
              </div>
              
              <ul className="text-[9.5px] text-[#302621] space-y-1.5 mt-1.5">
                {autoSpesifikasiList.map((spec, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-[#b48a3a] mr-2 mt-[2px] text-[8px]">●</span>
                    <span>{spec}</span>
                  </li>
                ))}
                {autoSpesifikasiList.length === 0 && <li className="flex items-start"><span className="text-[#b48a3a] mr-2 mt-[2px] text-[8px]">●</span>-</li>}
              </ul>
            </div>

            {/* Spesifikasi Tambahan */}
            <div className="sph-spec-box relative border border-[#e5d4b5] rounded-xl p-4 pt-6 bg-[#Fdfbf7]/30" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="absolute -top-3 left-4 flex items-center">
                <div className="bg-[#b48a3a] text-white py-1 px-2.5 rounded-md font-bold text-[9.5px] tracking-wider">
                  SPESIFIKASI TAMBAHAN
                </div>
              </div>
              
              <ul className="text-[9.5px] text-[#302621] space-y-1.5 mt-1.5">
                {tambahanList.map((spec, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-[#b48a3a] mr-2 mt-[2px] text-[8px]">●</span>
                    <span>{spec}</span>
                  </li>
                ))}
                {tambahanList.length === 0 && <li className="flex items-start"><span className="text-[#b48a3a] mr-2 mt-[2px] text-[8px]">●</span>-</li>}
              </ul>
            </div>
          </div>

          {/* Table */}
          <table className="w-full text-left border-separate border-spacing-0 mb-4 text-[10px]">
            <thead>
              <tr className="bg-[#b48a3a] text-white">
                <th className="py-2 px-2.5 font-bold w-8 text-center rounded-tl-lg">NO</th>
                <th className="py-2 px-2.5 font-bold">KETERANGAN PRODUK</th>
                <th className="py-2 px-2.5 font-bold text-center">VARIAN</th>
                <th className="py-2 px-2.5 font-bold text-center w-16">QTY (PCS)</th>
                <th className="py-2 px-2.5 font-bold text-center w-28">HARGA SATUAN</th>
                <th className="py-2 px-2.5 font-bold text-center w-28 rounded-tr-lg">SUB TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => {
                 const itemQty = parseFloat(String(item.qty || 0).replace(',','.'));
                 const itemPrice = parseFloat(String(item.final_harga_jual_per_pcs || item.harga_jual_per_pcs || 0));
                 const subTotal = itemQty * itemPrice;
                 return (
                   <tr key={i} className="group sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <td className="py-2 px-2 text-center text-slate-800 font-bold border-b border-slate-100 group-last:border-0">{i + 1}</td>
                    <td className="py-2 px-2 border-b border-slate-100 group-last:border-0">
                      <div className="font-bold text-[#302621]">{item.nama_product || '-'}</div>
                      {showKemasanInTable && (
                        <div className="text-[8px] text-slate-500 font-normal mt-0.5 leading-tight">
                          {item.is_fullset ? (
                            `Fullset ${item.fullset_nama || 'Kemasan Fullset'}`
                          ) : (
                            [
                              item.botol_nama && item.botol_nama !== 'Botol Dari Client' ? `Botol ${item.botol_nama}` : null,
                              item.tutup_nama && item.tutup_nama !== 'Tutup Dari Client' ? `Tutup ${item.tutup_nama}` : null,
                              item.spray_nama && item.spray_nama !== 'Spray Dari Client' ? `Spray ${item.spray_nama}` : null
                            ].filter(Boolean).join(' + ')
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center font-bold text-[#302621] border-b border-slate-100 group-last:border-0">{item.nama_varian || item.nama_product || '-'}</td>
                    <td className="py-2 px-2 text-center text-[#302621] border-b border-slate-100 group-last:border-0">{fmtQty(itemQty)}</td>
                    <td className="py-2 px-2 text-center text-[#302621] border-b border-slate-100 group-last:border-0">Rp {fmt(itemPrice)}</td>
                    <td className="py-2 px-2 text-center text-[#302621] border-b border-slate-100 group-last:border-0">Rp {fmt(subTotal)}</td>
                  </tr>
                 )
              })}
              
              {/* Legalitas & Jasa Rows */}
              {legalitasItems.map((leg, lIdx) => {
                 const legQty = leg.qty_variant || 1;
                 const legPrice = parseFloat(String(leg.harga_jual || 0));
                 const legSubTotal = legQty * legPrice;
                 return (
                   <tr key={`leg-${lIdx}`} className="group bg-amber-50/30 sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                    <td className="py-2 px-2 text-center text-slate-800 font-bold border-b border-slate-100">{items.length + lIdx + 1}</td>
                    <td className="py-2 px-2 border-b border-slate-100">
                      <div className="font-bold text-[#302621]">Layanan {leg.nama}</div>
                      {Array.isArray(leg.selected_variants) && leg.selected_variants.length > 0 ? (
                        <div className="text-[8px] text-amber-800 font-semibold mt-0.5">
                          Varian: {leg.selected_variants.join(', ')}
                        </div>
                      ) : (
                        <div className="text-[8px] text-amber-700 font-medium">Registrasi & Pengurusan Sertifikasi Legalitas Resmi</div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center font-semibold text-slate-600 border-b border-slate-100">Legalitas & Jasa</td>
                    <td className="py-2 px-2 text-center text-[#302621] font-bold border-b border-slate-100">{legQty} {(leg.nama?.toUpperCase().includes('BPOM') || leg.nama?.toUpperCase().includes('HALAL')) ? 'Varian' : 'Paket'}</td>
                    <td className="py-2 px-2 text-center text-[#302621] border-b border-slate-100">Rp {fmt(legPrice)}</td>
                    <td className="py-2 px-2 text-center text-[#302621] font-bold border-b border-slate-100">Rp {fmt(legSubTotal)}</td>
                  </tr>
                 );
              })}
              
              {/* Summary Rows */}
              {(() => {
                const productSubTotal = items.reduce((sum, item) => sum + (parseFloat(String(item.qty || 0).replace(',', '.')) * parseFloat(String(item.final_harga_jual_per_pcs || item.harga_jual_per_pcs || 0))), 0);
                const legalitasSubTotal = legalitasItems.reduce((sum, leg) => sum + (parseFloat(String(leg.harga_jual || 0)) * (leg.qty_variant || 1)), 0);
                const subTotalHarga = sphData.totalHargaJualSemua || (productSubTotal + legalitasSubTotal);
                const isExcludePpn = sphData.ppn_mode === 'exclude';
                const ppnPercentage = isExcludePpn ? (parseFloat(sphData.ppn_percentage) || 11) : 0;
                const ppnNominal = isExcludePpn ? (subTotalHarga * ppnPercentage / 100) : 0;
                const grandTotalHarga = subTotalHarga + ppnNominal;

                return (
                  <>
                    <tr className="sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <td colSpan="4" className="py-1.5"></td>
                      <td className="py-1.5 px-2 text-[9.5px] font-bold text-[#302621] text-right">SUB TOTAL</td>
                      <td className="py-1.5 px-2 text-[9.5px] font-bold text-[#302621] text-center border-b border-slate-100">Rp {fmt(subTotalHarga)}</td>
                    </tr>
                    <tr className="sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <td colSpan="4" className="py-1.5"></td>
                      <td className="py-1.5 px-2 text-[9.5px] font-bold text-[#302621] text-right border-b border-slate-100">PPN {ppnPercentage}%</td>
                      <td className="py-1.5 px-2 text-[9.5px] font-bold text-[#302621] text-center border-b border-slate-100">Rp {fmt(ppnNominal)}</td>
                    </tr>
                    <tr className="bg-[#b48a3a] text-white sph-no-break" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                      <td colSpan="4" className="py-2.5 bg-white"></td>
                      <td className="py-2.5 px-2 text-[9.5px] font-bold text-right rounded-bl-lg">TOTAL HARGA</td>
                      <td className="py-2.5 px-2 text-sm font-black text-center rounded-br-lg whitespace-nowrap">Rp {fmt(grandTotalHarga)}</td>
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>

          {/* Terms and Signature: Utuh sebagai 1 kesatuan, tidak akan terpotong horizontal */}
          <div 
            className={`sph-footer-block grid grid-cols-[1.5fr_1fr] gap-5 items-start mt-3 text-xs ${forceFooterPageBreak ? 'sph-force-page-break' : ''}`} 
            style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
          >
            
            {/* Notes */}
            <div className="sph-notes-box" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="flex items-center mb-1.5 border-b-[2px] border-[#e5d4b5] pb-1.5">
                <div className="bg-[#b48a3a] text-white w-5 h-5 rounded-full flex items-center justify-center mr-2 shrink-0">
                  <span className="font-bold text-xs">!</span>
                </div>
                <p className="font-bold text-[#302621] tracking-wider text-[10.5px]">CATATAN PENTING</p>
              </div>
              <ul className="text-[#302621] text-[9.5px] space-y-1.5 mt-2">
                {termsList.map((term, i) => (
                  <li key={i} className="flex items-start">
                    <span className="text-[#b48a3a] mr-2 mt-0.5">●</span>
                    <span className="leading-relaxed">{term}</span>
                  </li>
                ))}
                {termsList.length === 0 && <li className="flex items-start">-</li>}
              </ul>
            </div>

            {/* Signature */}
            <div 
              className="sph-signature-box relative border border-[#e5d4b5] rounded-xl p-3.5 bg-[#Fdfbf7]/30 min-h-[130px] flex flex-col justify-between" 
              style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
            >
              <div className="absolute -right-4 -bottom-4 opacity-[0.06] pointer-events-none overflow-hidden">
                <img src="/logo.png" alt="SDE Logo" className="w-28 h-28 object-contain grayscale" />
              </div>
              
              <div className="relative z-10">
                <p className="font-bold text-[#302621] text-[9.5px]">HORMAT KAMI,</p>
                <p className="font-bold text-[#302621] text-[9.5px]">PT Satala Dermatech Essential</p>
              </div>

              {/* Area tanda tangan */}
              <div className="my-0.5 h-12 flex items-center justify-start relative z-10">
                {activeSignatureUrl ? (
                  <img 
                    src={activeSignatureUrl} 
                    alt="Tanda Tangan" 
                    className="max-h-12 max-w-[120px] object-contain"
                    crossOrigin="anonymous" 
                  />
                ) : (
                  <div className="h-10"></div>
                )}
              </div>
              
              <div className="relative z-10">
                <p className="font-bold text-[#302621] underline text-[10.5px]">{picName || 'Muhammad Fadli'}</p>
                <p className="text-[8.5px] text-[#302621]">{picJabatan || 'Account Executive'}</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default SphPrintModal;
